import { Card } from "antd";
import { IntelligenceDto } from "dtos/application.dto";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";

type DebtCoverageProps = {
  debtcoverage: IntelligenceDto["behavioral_analysis"]["debt_coverage"];
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DebtCoverage = ({ debtcoverage }: DebtCoverageProps) => {
  const chartData = debtcoverage.monthly_breakdown.map((item) => ({
    ...item,
    label: `${MONTH_NAMES[item.month - 1]} ${item.year}`,
    dti: item.debt_coverage_ratio,
  }));

  return (
    <Card
      className="border border-gray-300"
      title={<div className="flex items-center gap-2 text-gray-800">Debt to Income Ratio Trend</div>}
    >
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="label"
              tick={{ fill: "#4b5563", fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tick={{ fill: "#4b5563", fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              domain={[0.8, 1.1]}
              tickFormatter={(v) => v.toFixed(2)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
              }}
              formatter={(value: number) => [value.toFixed(2), "DTI Ratio"]}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <ReferenceLine
              y={1}
              stroke="#94a3b8"
              strokeDasharray="4 4"
              label={{ value: "Break-even", fill: "#94a3b8", fontSize: 11 }}
            />
            <Line
              type="monotone"
              dataKey="dti"
              name="DTI Ratio"
              stroke="#ef4444"
              strokeWidth={2.5}
              dot={{ fill: "#dc2626", r: 5, strokeWidth: 0 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Stats footer */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 text-sm text-gray-600">
        <div className="text-center">
          <p className="text-xs text-gray-400 mb-0.5">Avg Income</p>
          <p className="font-semibold text-gray-800">${debtcoverage.average_monthly_income.toLocaleString()}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400 mb-0.5">Avg Debt</p>
          <p className="font-semibold text-gray-800">${debtcoverage.average_monthly_debt.toLocaleString()}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-400 mb-0.5">Avg DTI</p>
          <p className="font-semibold text-gray-800">{debtcoverage.average_debt_coverage_ratio.toFixed(2)}</p>
        </div>
      </div>
    </Card>
  );
};

export default DebtCoverage;
